import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-admin-rules-engine-supp-details',
    templateUrl: './admin-rules-engine-supp-details.component.html',
    styleUrls: ['./admin-rules-engine-supp-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminRulesEngineSuppDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  minimise = false;
  showFormLoader = true;
  filterParams: any = {};
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  modalTitle = 'Rules Engine – Before vs After';
  compareRows: Array<{ field: string; before: any; after: any }> = [];
  beforeRows: Array<{ key: string; value: any }> = [];
  afterRows: Array<{ key: string; value: any }> = [];

  private isInitialized = false;

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG) return;
    this.isInitialized = true;
    this.applyValidationRules();
  }

  private buildApplyPayload(): any {
    return {
      id: this.rowData?.ID,
      le_id: this.rowData?.LEID,
      me_id: this.rowData?.MEID,
      purchase_non_us: this.rowData?.PURCHASENONUS,
      purchase_us: this.rowData?.PURCHASEUS,
      tax_code: this.rowData?.TAXCODE,
      currency: this.rowData?.CURRENCY,
      puchase_base_curr: this.rowData?.PURCHASEBASECURR,
      taxable_income: this.rowData?.TAXABLEINCOME,
      tax_rate: this.rowData?.TAXRATE,
      total_tax: this.rowData?.TOTALTAX,
      xchange_rate: this.rowData?.XCHANGERATE
    };
  }

  private applyValidationRules(): void {
    const url = `${this.GENERAL_CONFIG.base_url}/applyValidationRules`;
    const payload = this.buildApplyPayload();
    this.http.post<any>(url, payload, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const afterObj = data?.callSuccess === '1' ? (data?.jsonObject || {}) : {};
        const beforeObj = this.rowData || {};
        const keys = Array.from(new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]));
        this.compareRows = keys.map((field) => ({ field, before: beforeObj?.[field], after: afterObj?.[field] }));
        this.beforeRows = keys.map((key) => ({ key, value: beforeObj?.[key] }));
        this.afterRows = keys.map((key) => ({ key, value: afterObj?.[key] }));
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      () => {
        const beforeObj = this.rowData || {};
        const keys = Object.keys(beforeObj);
        this.compareRows = keys.map((field) => ({ field, before: beforeObj?.[field], after: beforeObj?.[field] }));
        this.beforeRows = keys.map((key) => ({ key, value: beforeObj?.[key] }));
        this.afterRows = keys.map((key) => ({ key, value: beforeObj?.[key] }));
        this.showFormLoader = false;
        this.gtwSnackbarService.openSnackBar('Unable to apply validation rules', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
